// src/pages/api/API.js
import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import SwaggerUI from "swagger-ui";
import "swagger-ui/dist/swagger-ui.css";

export default function API() {
  const mountElRef = useRef(null);
  const uiRef = useRef(null); // holds the SwaggerUI instance
  const [err, setErr] = useState(null);
  const [group, setGroup] = useState("Customers");

  const GROUPS = [
    "Customers",
    "Managers",
    "Subaccounts",
    "Offers",
    "Offer Items",
    "SIM",
    "Watchdog",
    "Authentication",
  ];

  // 1) Mount SwaggerUI once
  useEffect(() => {
    uiRef.current = SwaggerUI({
      domNode: mountElRef.current,
      spec: {
        openapi: "3.0.0",
        info: { title: "Loading…", version: "v1" },
        paths: {},
      },
      deepLinking: true,
      docExpansion: "list",
      defaultModelsExpandDepth: -1,
      tryItOutEnabled: false,
      supportedSubmitMethods: [],
      persistAuthorization: true,
      layout: "BaseLayout",
      validatorUrl: null,
    });

    return () => {
      // robust unmount (works even if .destroy doesn't exist)
      try {
        const sys = uiRef.current?.getSystem?.();
        if (sys?.unmount) sys.unmount();
      } catch {}
      if (mountElRef.current) mountElRef.current.innerHTML = "";
      uiRef.current = null;
    };
  }, []);

  // 2) On group change, fetch new spec and replace it
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setErr(null);
        const res = await fetch(
          `/swagger.json?group=${encodeURIComponent(group)}&ts=${Date.now()}`,
          {
            headers: { Accept: "application/json" },
            cache: "no-store",
          }
        );
        if (!res.ok)
          throw new Error(`Spec fetch failed: ${res.status} ${res.statusText}`);
        const spec = await res.json();

        // patch base so "Try it out" hits your proxy (adjust to "/" if you use package.json "proxy")
        const patched = spec.openapi?.startsWith?.("3.")
          ? { ...spec, servers: [{ url: "/api" }] }
          : spec.swagger === "2.0"
          ? (() => {
              const { protocol, host } = window.location;
              return {
                ...spec,
                host,
                schemes: [protocol.replace(":", "")],
                basePath: "/api",
              };
            })()
          : spec;

        if (cancelled) return;
        // Official way to update spec without recreating the UI
        const sys = uiRef.current?.getSystem?.();
        sys?.specActions?.updateJsonSpec?.(patched);
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [group]);

  return (
    <div className="swagger-wrap">
      <Form.Group className="mb-5">
        <Form.Label>Group:</Form.Label>
        <Form.Select value={group} onChange={(e) => setGroup(e.target.value)}>
          {GROUPS.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {err ? <pre style={{ whiteSpace: "pre-wrap" }}>{err}</pre> : null}
      <div ref={mountElRef} />
    </div>
  );
}
